import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MessageCircle, User, Users, ExternalLink } from "lucide-react";
import Logo from "./Logo";

const NAV = [
    { label: "Início", to: "/" },
    { label: "Soluções", to: "/solucoes" },
    { label: "Quem Somos", to: "/quem-somos" },
    { label: "Contato", to: "/contato" },
];

const LEGAL = [
    { label: "Política de Privacidade", to: "/politica-de-privacidade" },
    { label: "Termos de Uso", to: "/termos-de-uso" },
];

const CONTATOS = [
    { icon: Mail, label: "E-mail", valor: "contato@smartbeneficios.com.br", href: "mailto:contato@smartbeneficios.com.br" },
    { icon: Phone, label: "Telefone", valor: "(11) 91675-1213", href: "tel:+5511916751213" },
    { icon: MessageCircle, label: "WhatsApp", valor: "(11) 91675-1213", href: "https://wa.me/5511916751213" },
];

const ACESSOS = [
    { label: "Cliente", href: "https://smart-vt-flow.base44.app/login?perfil=empresa", icon: User, testid: "footer-acesso-cliente" },
    { label: "Funcionário", href: "https://smart-vt-flow.base44.app/login?perfil=funcionario", icon: Users, testid: "footer-acesso-funcionario" },
];

const INSTAGRAM_URL = "https://www.instagram.com/smartbeneficios_/";

export default function Footer() {
    return (
        <footer className="grain relative bg-azul-950 text-white" data-testid="footer">
            <div className="container-x relative z-10 grid gap-12 py-16 md:grid-cols-12 md:py-20">
                <div className="md:col-span-5">
                    <Logo dark />
                    <p className="mt-5 max-w-sm text-lg font-medium leading-relaxed text-white/80">
                        Uma empresa. Uma plataforma. Uma mensalidade.
                    </p>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/55">
                        Seu custo não precisa crescer junto com seus benefícios. Planos a partir de R$ 99/mês.
                    </p>
                    <div className="mt-6" data-testid="footer-social">
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-social-instagram"
                            aria-label="Instagram da Smart Benefícios (@smartbeneficios_)"
                            className="group inline-flex items-center gap-3 rounded-full border border-white/25 py-2 pr-7 pl-2 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:border-laranja-400 hover:text-laranja-300"
                        >
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-colors duration-200 group-hover:bg-laranja-500 group-hover:text-white">
                                <Instagram className="h-4 w-4" strokeWidth={1.9} />
                            </span>
                            Instagram
                        </a>
                    </div>
                </div>

                <div className="md:col-span-3">
                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-laranja-400">Navegação</h3>
                    <ul className="mt-5 space-y-3">
                        {NAV.map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-link-${l.to === "/" ? "inicio" : l.to.slice(1)}`}
                                    className="text-sm font-medium text-white/75 transition-colors hover:text-white"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <ul className="mt-8 space-y-3">
                        {LEGAL.map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-link-${l.to.slice(1)}`}
                                    className="text-sm font-medium text-white/55 transition-colors hover:text-white"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-laranja-400">Contato</h3>
                    <ul className="mt-5 space-y-3.5" data-testid="footer-contatos">
                        {CONTATOS.map((c) => (
                            <li key={c.label}>
                                <a
                                    href={c.href}
                                    target={c.href.startsWith("http") ? "_blank" : undefined}
                                    rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                                    data-testid={`footer-contato-${c.label.toLowerCase()}`}
                                    className="group flex items-center gap-2.5 text-sm text-white/75 transition-colors hover:text-white"
                                >
                                    <c.icon className="h-4 w-4 shrink-0 text-laranja-400" />
                                    <span className="font-semibold text-white/90">{c.label}</span>
                                    <span className="transition-colors group-hover:text-laranja-300">{c.valor}</span>
                                </a>
                            </li>
                        ))}
                        <li className="flex items-center gap-2.5 text-sm text-white/70" data-testid="footer-cnpj">
                            <span className="font-semibold text-white/90">CNPJ</span>
                            <span>29.767.611/0001-41</span>
                        </li>
                    </ul>

                    <h3 className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-laranja-400">Acessar plataforma</h3>
                    <ul className="mt-5 space-y-3" data-testid="footer-acessos">
                        {ACESSOS.map((a) => (
                            <li key={a.href}>
                                <a
                                    href={a.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={a.testid}
                                    className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 py-2 pl-4 pr-5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:border-laranja-400 hover:text-laranja-300"
                                >
                                    <a.icon className="h-4 w-4 text-laranja-400" />
                                    {a.label}
                                    <ExternalLink className="h-3 w-3 opacity-60 transition-opacity group-hover:opacity-100" />
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="relative z-10 border-t border-white/10">
                <div className="container-x flex flex-col items-center justify-between gap-2 py-6 text-xs text-white/45 sm:flex-row">
                    <span data-testid="footer-copyright">© {new Date().getFullYear()} Smart Benefícios. Todos os direitos reservados.</span>
                    <span>Planos a partir de R$ 99/mês · Uma plataforma para toda a gestão</span>
                </div>
            </div>
        </footer>
    );
}
